import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, ActivatedRoute } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { Button } from 'ustore-ui';

import { ConfirmEmailService } from '../../core/services/confirm-email.service';
import { ToastService } from '../../shared/services/toast.service';
import { ToastComponent } from '../../shared/components/toast/toast.component';
import { AuthLayoutComponent } from "../../shared/layouts/auth-layout.component";
import { FooterComponent } from "../../shared/components/footer/footer.component";
import { I18nService } from '../../core/services/i18n.service';
import { I18nPipe } from '../../shared/pipes/i18n.pipe';
import { UtmPreservationService } from '../../core/services/utm-preservation.service';

@Component({
  selector: 'umkt-confirm-email',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    Button,
    RouterModule,
    ToastComponent,
    AuthLayoutComponent,
    FooterComponent,
    I18nPipe
],
  templateUrl: './confirm-email.component.html',
  styleUrls: ['../../../assets/style/login-register.component.scss'],
})
export class ConfirmEmailComponent implements OnInit {
  
  isLoading = true;
  redirectDelay = 5000; 
  token: string = '';
  confirmationStatus: 'pending' | 'success' | 'error' = 'pending';
  errorMessage: string = '';
  private isProcessingRequest = false;
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private confirmEmailService = inject(ConfirmEmailService);
  private toastService = inject(ToastService);
  private i18nService = inject(I18nService);
  private utmPreservationService = inject(UtmPreservationService);


  ngOnInit(): void {
    // Fix malformed URL if backend added token incorrectly
    this.fixMalformedUrl();
    
    this.route.queryParams.subscribe(queryParams => {
      // Try to get token from query params
      this.token = queryParams['token'];
      
      // If token is not found, check if it's in a malformed utm_source param
      if (!this.token || this.token.trim() === '') {
        const utmSource = queryParams['utm_source'];
        if (utmSource) {
          // Check if utm_source contains encoded token (%3Ftoken%3D)
          if (utmSource.includes('%3Ftoken%3D') || utmSource.includes('%3ftoken%3d')) {
            try {
              const decoded = decodeURIComponent(utmSource);
              if (decoded.includes('?token=')) {
                const parts = decoded.split('?token=');
                if (parts.length > 1) {
                  this.token = parts[1];
                }
              }
            } catch (e) {
              console.error('Error decoding utm_source:', e);
            }
          }
          // Check if utm_source contains unencoded token (?token=)
          else if (utmSource.includes('?token=')) {
            const parts = utmSource.split('?token=');
            if (parts.length > 1) {
              this.token = parts[1];
            }
          }
        }
      }
      
      if (this.token && this.token.trim() !== '') {
        this.confirmEmail();
      } else {
        this.errorMessage = 'pages.confirmEmail.messages.tokenNotFound';
        this.confirmationStatus = 'error';
        this.isLoading = false;
      }
    });
  }

  private fixMalformedUrl(): void {
    if (typeof window === 'undefined') {
      return;
    }

    const currentUrl = window.location.href;
    const urlObj = new URL(currentUrl);
    const searchParams = urlObj.searchParams;
    
    // Check if utm_source contains a malformed token (has ?token= encoded)
    const utmSource = searchParams.get('utm_source');
    if (utmSource && utmSource.includes('?token=')) {
      // Extract token from malformed utm_source
      const parts = utmSource.split('?token=');
      const actualUtmSource = parts[0];
      const token = parts[1];
      
      // Fix the URL by setting correct params
      searchParams.set('utm_source', actualUtmSource);
      if (token) {
        searchParams.set('token', token);
      }
      
      // Rebuild URL with correct params
      const fixedUrl = urlObj.pathname + '?' + searchParams.toString();
      
      // Navigate to fixed URL
      this.router.navigateByUrl(fixedUrl, { replaceUrl: true });
      return;
    }
    
    // Also check for encoded malformed URL (%3Ftoken%3D)
    if (utmSource && (utmSource.includes('%3Ftoken%3D') || utmSource.includes('%3ftoken%3d'))) {
      // Decode and extract
      const decoded = decodeURIComponent(utmSource);
      if (decoded.includes('?token=')) {
        const parts = decoded.split('?token=');
        const actualUtmSource = parts[0];
        const token = parts[1];
        
        searchParams.set('utm_source', actualUtmSource);
        if (token) {
          searchParams.set('token', token);
        }
        
        const fixedUrl = urlObj.pathname + '?' + searchParams.toString();
        this.router.navigateByUrl(fixedUrl, { replaceUrl: true });
        return;
      }
    }
    
  }

  private confirmEmail(): void {
    if (this.isProcessingRequest) {
      return;
    }

    this.isProcessingRequest = true;
    this.isLoading = true;
    this.confirmationStatus = 'pending';
    
    this.confirmEmailService.confirmEmail(this.token).subscribe({
      next: (response) => {
        this.isLoading = false;
        if (response.error) {
          this.confirmationStatus = 'error';
          this.errorMessage = response.msg || 'pages.confirmEmail.messages.defaultError';
          this.toastService.showError(
            this.i18nService.instant(this.errorMessage),
            5000,
            'top-center'
          );
        } else {
          this.confirmationStatus = 'success';
          this.toastService.showSuccess(
            this.i18nService.instant('pages.confirmEmail.messages.success'),
            3000,
            'top-center'
          );
          this.startRedirect();
        }
      },
      error: (error) => {
        this.isLoading = false;
        this.confirmationStatus = 'error';
        
        if (error.error && error.error.msg) {
          this.errorMessage = error.error.msg;
        } else if (error.error && error.error.message) {
          this.errorMessage = error.error.message;
        } else {
          this.errorMessage = 'pages.confirmEmail.messages.genericError';
        }
        
        console.error('Erro ao confirmar email:', error);
        this.toastService.showError(
          this.i18nService.instant(this.errorMessage),
          5000,
          'top-center'
        );
      }
    });
  }

  private startRedirect(): void {
    setTimeout(() => {
      this.redirectToLogin();
    }, this.redirectDelay);
  }

  redirectToLogin(): void {
    this.isLoading = false;
    
    // Only add UTM params if they exist
    const queryParams: Record<string, string> = {};
    
    // Get preserved UTM params
    const preservedParams = this.utmPreservationService.preservedUtmParams;
    if (preservedParams && Object.keys(preservedParams).length > 0) {
      Object.entries(preservedParams).forEach(([key, value]) => {
        if (key.toLowerCase().startsWith('utm_')) {
          // Ensure utm_source doesn't contain token
          if (key.toLowerCase() === 'utm_source') {
            // Remove any token that might be embedded in utm_source
            let cleanValue = value;
            if (cleanValue.includes('?token=')) {
              cleanValue = cleanValue.split('?token=')[0];
            }
            if (cleanValue.includes('%3Ftoken%3D') || cleanValue.includes('%3ftoken%3d')) {
              try {
                const decoded = decodeURIComponent(cleanValue);
                if (decoded.includes('?token=')) {
                  cleanValue = decoded.split('?token=')[0];
                }
              } catch (e) {
                // Ignore decode errors
              }
            }
            queryParams[key] = cleanValue;
          } else {
            queryParams[key] = value;
          }
        }
      });
    }
    
    // If no preserved params, check current URL params (but exclude token)
    if (Object.keys(queryParams).length === 0 && typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      urlParams.forEach((value, key) => {
        if (key.toLowerCase().startsWith('utm_') && key.toLowerCase() !== 'token') {
          // Clean utm_source if it contains token
          if (key.toLowerCase() === 'utm_source') {
            let cleanValue = value;
            if (cleanValue.includes('?token=')) {
              cleanValue = cleanValue.split('?token=')[0];
            }
            if (cleanValue.includes('%3Ftoken%3D') || cleanValue.includes('%3ftoken%3d')) {
              try {
                const decoded = decodeURIComponent(cleanValue);
                if (decoded.includes('?token=')) {
                  cleanValue = decoded.split('?token=')[0];
                }
              } catch (e) {
                // Ignore decode errors
              }
            }
            queryParams[key] = cleanValue;
          } else {
            queryParams[key] = value;
          }
        }
      });
    }
    
    // Only navigate with query params if there are UTM params
    if (Object.keys(queryParams).length > 0) {
      this.router.navigate(['/login'], { queryParams });
    } else {
      this.router.navigate(['/login']);
    }
  }

  enterNow(): void {
    this.redirectToLogin();
  }
}
